'use client';

/**
 * Generation Gallery Component
 *
 * Displays user's generated media with Images/Videos tabs and Favorites filter.
 * Shows progress cards for in-progress generations with live polling.
 * Completed generations display in a responsive grid with zoom/download options.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Heart,
  Image as ImageIcon,
  Loader2,
  Maximize2,
  Server,
  Sparkles,
  Trash2,
  Upload,
  Video,
  Wand2,
  X,
} from 'lucide-react';

import { useInvalidateCoinBalance } from '~/components/wallet';
import {
  type Generation,
  cancelGeneration,
  deleteGeneration,
  getActiveGenerations,
  getUserGenerations,
} from '~/lib/server/actions/generate-actions';
import { useSmoothProgress } from '~/lib/use-smooth-progress';

type MediaTab = 'images' | 'videos';

interface GenerationGalleryProps {
  onRefresh?: () => void;
  refreshTrigger?: number;
  /** When set, auto-switch to this tab on next refresh */
  activeMediaType?: 'image' | 'video';
}

// Status configuration for progress display
const STATUS_CONFIG: Record<
  Generation['status'],
  {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    desc: string;
    progress: number;
    color: string;
  }
> = {
  pending: {
    icon: Clock,
    label: 'Queued',
    desc: 'Waiting in queue...',
    progress: 5,
    color: 'text-amber-400',
  },
  creating_pod: {
    icon: Server,
    label: 'Starting GPU',
    desc: 'Securing a GPU server...',
    progress: 15,
    color: 'text-blue-400',
  },
  uploading_reference: {
    icon: Upload,
    label: 'Uploading',
    desc: 'Uploading reference image...',
    progress: 30,
    color: 'text-cyan-400',
  },
  generating: {
    icon: Wand2,
    label: 'Generating Image',
    desc: 'AI is creating your image...',
    progress: 60,
    color: 'text-purple-400',
  },
  generating_video: {
    icon: Video,
    label: 'Creating Video',
    desc: 'Rendering video from image...',
    progress: 75,
    color: 'text-purple-400',
  },
  uploading: {
    icon: Upload,
    label: 'Almost Done',
    desc: 'Saving your creation...',
    progress: 90,
    color: 'text-cyan-400',
  },
  completed: {
    icon: CheckCircle,
    label: 'Complete',
    desc: 'Done!',
    progress: 100,
    color: 'text-green-400',
  },
  failed: {
    icon: AlertCircle,
    label: 'Failed',
    desc: 'Something went wrong',
    progress: 0,
    color: 'text-red-400',
  },
};

const ACTIVE_STATUSES = new Set<Generation['status']>([
  'pending',
  'creating_pod',
  'uploading_reference',
  'generating',
  'generating_video',
  'uploading',
]);

export function GenerationGallery({
  onRefresh,
  refreshTrigger,
  activeMediaType,
}: GenerationGalleryProps) {
  const [activeTab, setActiveTab] = useState<MediaTab>('images');
  const [showFavorites, setShowFavorites] = useState(false);
  const [selectedGeneration, setSelectedGeneration] =
    useState<Generation | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const invalidateCoinBalance = useInvalidateCoinBalance();
  const queryClient = useQueryClient();

  // Cached gallery fetch — 30s staleTime so revisiting doesn't re-fetch
  const {
    data: generationsData,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['user-generations', refreshTrigger],
    queryFn: async () => {
      const result = await getUserGenerations(50, 0);
      if (result.success) return result.generations;
      if (result.error === 'Not authenticated') return [];
      throw new Error(result.error || 'Failed to load generations');
    },
    staleTime: 30 * 1000, // 30s — fresh enough for gallery
    gcTime: 5 * 60 * 1000,
  });

  const [generations, setGenerations] = useState<Generation[]>([]);
  const error = queryError ? (queryError as Error).message : null;

  // Sync query data to local state (needed for polling updates)
  useEffect(() => {
    if (generationsData) {
      setGenerations(generationsData);
    }
  }, [generationsData]);

  // For polling/refresh callbacks
  const loadGenerations = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['user-generations'] });
  }, [queryClient]);

  // Poll for active generation updates
  const pollActiveGenerations = useCallback(async () => {
    try {
      const result = await getActiveGenerations();
      // Skip polling if not authenticated
      if (!result.success && result.error === 'Not authenticated') {
        return false;
      }
      if (!result.success) return false;

      const stillActiveIds = new Set(result.generations.map((g) => g.id));

      setGenerations((prev) => {
        const updated = prev.map((g) =>
          // A row we were showing as in-flight that the server no longer
          // reports as active has finished. Retire the spinner immediately
          // instead of waiting for the gallery refetch — that refetch is
          // served from a 30s-stale cache, so the card could otherwise spin
          // for half a minute (or forever, if the refetch failed) next to
          // the finished video.
          !stillActiveIds.has(g.id) && ACTIVE_STATUSES.has(g.status)
            ? { ...g, status: 'completed' as const }
            : g,
        );
        for (const activeGen of result.generations) {
          const index = updated.findIndex((g) => g.id === activeGen.id);
          if (index >= 0) updated[index] = activeGen;
          else updated.unshift(activeGen);
        }
        return updated;
      });

      return result.generations.length > 0;
    } catch (err) {
      console.error('Failed to poll generations:', err);
      return false;
    }
  }, []);

  // useQuery handles initial load + refreshTrigger via queryKey

  // Auto-switch tab when a new generation is triggered with a specific media type
  useEffect(() => {
    if (activeMediaType && refreshTrigger) {
      setActiveTab(activeMediaType === 'video' ? 'videos' : 'images');
    }
  }, [activeMediaType, refreshTrigger]);

  // Set up polling when there are active generations
  useEffect(() => {
    const hasActive = generations.some((g) => ACTIVE_STATUSES.has(g.status));

    if (hasActive) {
      // Poll every 2 seconds when there are active generations
      pollingRef.current = setInterval(async () => {
        const stillActive = await pollActiveGenerations();
        if (!stillActive) {
          // All done, reload full list to ensure we have latest data
          loadGenerations();
          // Refresh coin balance since generation completed and coins were deducted
          invalidateCoinBalance();
        }
      }, 2000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [generations, pollActiveGenerations, loadGenerations]);

  // Filter generations by tab and favorites
  const filteredGenerations = generations.filter((gen) => {
    const matchesTab =
      activeTab === 'images'
        ? gen.media_type === 'image'
        : gen.media_type === 'video';
    const matchesFavorites = !showFavorites || favorites.has(gen.id);
    return matchesTab && matchesFavorites;
  });

  // Separate active and completed generations for display
  const activeGenerations = filteredGenerations.filter((g) =>
    ACTIVE_STATUSES.has(g.status),
  );
  const completedGenerations = filteredGenerations.filter(
    (g) => g.status === 'completed' || g.status === 'failed',
  );

  const toggleFavorite = (id: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCancel = async (id: string) => {
    try {
      const result = await cancelGeneration(id);
      if (result.success) {
        // Remove from generations list
        setGenerations((prev) => prev.filter((g) => g.id !== id));
        // Refresh coin balance since coins were refunded
        invalidateCoinBalance();
      }
    } catch (err) {
      console.error('Failed to cancel generation:', err);
    }
  };

  const handleDelete = async (id: string) => {
    const result = await deleteGeneration(id);
    if (result.success) {
      setGenerations((prev) => prev.filter((g) => g.id !== id));
      // Close lightbox if the deleted generation was being viewed
      if (selectedGeneration?.id === id) {
        setSelectedGeneration(null);
      }
    }
  };

  const imageCount = generations.filter((g) => g.media_type === 'image').length;
  const videoCount = generations.filter((g) => g.media_type === 'video').length;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between sm:mb-4">
        <h2 className="text-lg font-bold text-white sm:text-xl">
          Your Gallery
        </h2>

        {/* Favorites Toggle */}
        <button
          onClick={() => setShowFavorites(!showFavorites)}
          className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-all ${
            showFavorites
              ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
              : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
          }`}
        >
          <Heart className={`h-4 w-4 ${showFavorites ? 'fill-current' : ''}`} />
          Favorites
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-3 flex gap-2 sm:mb-4">
        <button
          onClick={() => setActiveTab('images')}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all sm:gap-2 sm:px-4 sm:py-2 ${
            activeTab === 'images'
              ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
              : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
          }`}
        >
          <ImageIcon className="h-4 w-4" />
          Images
          <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-xs">
            {imageCount}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('videos')}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all sm:gap-2 sm:px-4 sm:py-2 ${
            activeTab === 'videos'
              ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
              : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
          }`}
        >
          <Video className="h-4 w-4" />
          Videos
          <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-xs">
            {videoCount}
          </span>
        </button>
      </div>

      {/* Gallery Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="text-eros-pink h-8 w-8 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex h-64 flex-col items-center justify-center text-center">
            <AlertCircle className="mb-3 h-12 w-12 text-red-400" />
            <p className="mb-2 text-red-400">{error}</p>
            <button
              onClick={loadGenerations}
              className="rounded-lg bg-white/10 px-4 py-2 text-sm text-white transition-colors hover:bg-white/20"
            >
              Try Again
            </button>
          </div>
        ) : filteredGenerations.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/5">
              <Sparkles className="h-10 w-10 text-gray-500" />
            </div>
            <p className="mb-2 text-gray-400">
              {showFavorites
                ? `No favorite ${activeTab} yet`
                : `No ${activeTab} generated yet`}
            </p>
            <p className="text-sm text-gray-500">
              Generate your first {activeTab === 'images' ? 'image' : 'video'}{' '}
              to see it here
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Active/In-Progress Generations */}
            {activeGenerations.length > 0 && (
              <div>
                <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  In Progress ({activeGenerations.length})
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                  <AnimatePresence mode="popLayout">
                    {activeGenerations.map((generation, index) => (
                      <ProgressCard
                        key={generation.id}
                        generation={generation}
                        delay={index * 0.05}
                        onCancel={handleCancel}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* Completed/Failed Generations */}
            {completedGenerations.length > 0 && (
              <div>
                {activeGenerations.length > 0 && (
                  <h3 className="mb-3 text-sm font-medium text-gray-400">
                    Completed ({completedGenerations.length})
                  </h3>
                )}
                <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
                  <AnimatePresence mode="popLayout">
                    {completedGenerations.map((generation, index) => (
                      <GalleryCard
                        key={generation.id}
                        generation={generation}
                        isFavorite={favorites.has(generation.id)}
                        onToggleFavorite={() => toggleFavorite(generation.id)}
                        onView={() => setSelectedGeneration(generation)}
                        onDelete={() => handleDelete(generation.id)}
                        delay={index * 0.05}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedGeneration && (
          <GenerationLightbox
            generation={selectedGeneration}
            isFavorite={favorites.has(selectedGeneration.id)}
            onToggleFavorite={() => toggleFavorite(selectedGeneration.id)}
            onDelete={() => handleDelete(selectedGeneration.id)}
            onClose={() => setSelectedGeneration(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Progress Card for in-progress generations
interface ProgressCardProps {
  generation: Generation;
  delay: number;
  onCancel: (id: string) => void;
}

function ProgressCard({ generation, delay, onCancel }: ProgressCardProps) {
  const [isCancelling, setIsCancelling] = useState(false);
  const config = STATUS_CONFIG[generation.status];
  const StatusIcon = config.icon;
  // Time-driven bar seeded from the row's creation time (survives refresh),
  // floored by the status milestone, slowing as it nears the end.
  const smoothPct = useSmoothProgress({
    active: true,
    done: false,
    expectedSeconds: generation.media_type === 'video' ? 240 : 50,
    floor: config.progress,
    startedAt: new Date(generation.created_at).getTime(),
  });

  const handleCancel = async () => {
    if (isCancelling) return;
    setIsCancelling(true);
    await onCancel(generation.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ delay }}
      className="relative overflow-hidden rounded-xl border border-white/10"
    >
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900">
        <div className="absolute top-1/4 left-1/4 h-24 w-24 animate-pulse rounded-full bg-pink-600/15 blur-3xl" />
        <div
          className="absolute right-1/4 bottom-1/4 h-32 w-32 animate-pulse rounded-full bg-purple-600/15 blur-3xl"
          style={{ animationDelay: '300ms' }}
        />
      </div>

      <div className="relative p-4">
        {/* Cancel button */}
        <button
          onClick={handleCancel}
          disabled={isCancelling}
          className="absolute top-3 right-3 z-10 rounded-full bg-white/10 p-1.5 transition-colors hover:bg-red-500/30"
          title="Cancel generation"
        >
          {isCancelling ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : (
            <X className="h-4 w-4 text-gray-400 hover:text-red-300" />
          )}
        </button>

        {/* Spinner + Status */}
        <div className="mb-3 flex items-center gap-3 pr-8">
          {/* Animated spinner */}
          <div className="flex-shrink-0">
            <div className="h-10 w-10 animate-spin rounded-full border-3 border-gray-700 border-t-pink-500" />
          </div>

          <div className="min-w-0 flex-1">
            <p className={`text-sm font-semibold ${config.color}`}>
              {config.label}
            </p>
            <p className="mt-0.5 text-xs text-gray-400">{config.desc}</p>
          </div>
        </div>

        {/* Generation info */}
        <div className="mb-3">
          <p className="truncate text-sm font-medium text-white">
            {generation.character?.name || 'Custom Generation'}
          </p>
          <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">
            {generation.prompt}
          </p>
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
            <motion.div
              className="h-full bg-gradient-to-r from-pink-500 to-purple-500"
              initial={{ width: 0 }}
              animate={{ width: `${smoothPct}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{smoothPct}%</span>
            {isCancelling && (
              <span className="text-xs text-red-400">Cancelling...</span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Gallery Card Component for completed generations
interface GalleryCardProps {
  generation: Generation;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onView: () => void;
  onDelete: () => void;
  delay: number;
}

function GalleryCard({
  generation,
  isFavorite,
  onToggleFavorite,
  onView,
  onDelete,
  delay,
}: GalleryCardProps) {
  const isCompleted =
    generation.status === 'completed' && generation.result_url;
  const isFailed = generation.status === 'failed';
  const isVideo = generation.media_type === 'video';
  const videoRef = useRef<HTMLVideoElement>(null);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ delay }}
      className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/5"
      onMouseEnter={() => {
        if (isVideo && videoRef.current) {
          videoRef.current.play().catch(() => {});
        }
      }}
      onMouseLeave={() => {
        if (isVideo && videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
      }}
    >
      {/* Image/Video/Placeholder */}
      <div className="relative aspect-[3/4]">
        {isCompleted && isVideo ? (
          <>
            <video
              ref={videoRef}
              src={generation.result_url!}
              poster={generation.thumbnail_url || undefined}
              muted
              loop
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
            />
            {/* Video badge */}
            <div className="absolute top-2 left-2 flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-xs text-white">
              <Video className="h-3 w-3" />
              Video
            </div>
          </>
        ) : isCompleted ? (
          <img
            src={generation.thumbnail_url || generation.result_url!}
            alt="Generated media"
            className="h-full w-full object-cover"
          />
        ) : isFailed ? (
          <div className="flex h-full w-full flex-col items-center justify-center bg-red-500/10">
            <AlertCircle className="mb-2 h-8 w-8 text-red-400" />
            <span className="text-sm text-red-400">Failed</span>
            {generation.error_message && (
              <span className="mt-1 line-clamp-2 px-2 text-center text-xs text-red-400/70">
                {generation.error_message}
              </span>
            )}
          </div>
        ) : (
          <div className="h-full w-full bg-gray-800" />
        )}

        {/* Overlay on hover */}
        {isCompleted && (
          <div className="absolute inset-0 flex items-center justify-center gap-3 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100 sm:gap-2">
            <button
              onClick={onView}
              className="rounded-full bg-white/20 p-2.5 transition-colors hover:bg-white/30 sm:p-2"
            >
              <Maximize2 className="h-5 w-5 text-white" />
            </button>
            <a
              href={generation.result_url!}
              download
              className="rounded-full bg-white/20 p-2.5 transition-colors hover:bg-white/30 sm:p-2"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="h-5 w-5 text-white" />
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="rounded-full bg-red-500/30 p-2.5 transition-colors hover:bg-red-500/50 sm:p-2"
            >
              <Trash2 className="h-5 w-5 text-red-300" />
            </button>
          </div>
        )}

        {/* Delete button for failed generations */}
        {isFailed && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute top-2 right-2 rounded-full bg-red-500/30 p-1.5 transition-colors hover:bg-red-500/50"
          >
            <Trash2 className="h-4 w-4 text-red-300" />
          </button>
        )}
      </div>

      {/* Favorite button */}
      {isCompleted && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite();
          }}
          className="absolute top-2 right-2 rounded-full bg-black/40 p-1.5 transition-colors hover:bg-black/60"
        >
          <Heart
            className={`h-4 w-4 transition-colors ${
              isFavorite ? 'text-eros-pink fill-eros-pink' : 'text-white'
            }`}
          />
        </button>
      )}

      {/* Info bar */}
      <div className="bg-black/40 p-2">
        <div className="flex items-center justify-between">
          <span className="truncate text-xs text-gray-400">
            {generation.character?.name || 'Custom'}
          </span>
          <span className="text-xs text-gray-500">
            {generation.cost_coins} coins
          </span>
        </div>
      </div>
    </motion.div>
  );
}

// Lightbox Component
interface GenerationLightboxProps {
  generation: Generation;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onDelete: () => void;
  onClose: () => void;
}

function GenerationLightbox({
  generation,
  isFavorite,
  onToggleFavorite,
  onDelete,
  onClose,
}: GenerationLightboxProps) {
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="relative flex max-h-[90vh] max-w-4xl flex-col items-center gap-3 overflow-y-auto md:flex-row md:items-start md:gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button - always top right */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 z-10 rounded-full bg-white/10 p-2 transition-colors hover:bg-white/20 md:-top-12"
        >
          <X className="h-6 w-6 text-white" />
        </button>

        {/* Left side on desktop, hidden on mobile (shown below image instead) */}
        <div className="hidden w-56 flex-shrink-0 flex-col gap-3 pt-2 md:flex">
          {/* Generation Info */}
          <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3">
            <p className="mb-1 text-sm font-medium text-white">
              {generation.character?.name || 'Custom Generation'}
            </p>
            <p className="line-clamp-4 text-xs text-gray-400">
              {generation.prompt}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleFavorite}
              className="rounded-full border border-white/10 bg-white/5 p-2 transition-colors hover:bg-white/10"
            >
              <Heart
                className={`h-5 w-5 ${
                  isFavorite ? 'text-eros-pink fill-eros-pink' : 'text-white'
                }`}
              />
            </button>
            <a
              href={generation.result_url!}
              download
              className="rounded-full border border-white/10 bg-white/5 p-2 transition-colors hover:bg-white/10"
            >
              <Download className="h-5 w-5 text-white" />
            </a>
            <button
              onClick={onDelete}
              className="rounded-full border border-red-500/20 bg-white/5 p-2 transition-colors hover:bg-red-500/20"
            >
              <Trash2 className="h-5 w-5 text-red-400" />
            </button>
          </div>
        </div>

        {/* Image or Video */}
        <div className="relative min-w-0 flex-shrink">
          {generation.result_url && generation.media_type === 'video' ? (
            <video
              src={generation.result_url}
              poster={generation.thumbnail_url || undefined}
              controls
              autoPlay
              loop
              playsInline
              className="h-auto max-h-[60vh] max-w-full rounded-xl object-contain shadow-2xl shadow-black/50 md:max-h-[85vh]"
            />
          ) : generation.result_url ? (
            <img
              src={generation.result_url}
              alt="Generated media"
              className="h-auto max-h-[60vh] max-w-full rounded-xl object-contain shadow-2xl shadow-black/50 md:max-h-[85vh]"
            />
          ) : null}
        </div>

        {/* Mobile only - Info & Controls below image */}
        <div className="flex w-full flex-col items-center gap-2 md:hidden">
          <div className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2">
            <p className="mb-0.5 text-sm font-medium text-white">
              {generation.character?.name || 'Custom Generation'}
            </p>
            <p className="line-clamp-2 text-xs text-gray-400">
              {generation.prompt}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleFavorite}
              className="rounded-full border border-white/10 bg-white/5 p-2 transition-colors hover:bg-white/10"
            >
              <Heart
                className={`h-5 w-5 ${
                  isFavorite ? 'text-eros-pink fill-eros-pink' : 'text-white'
                }`}
              />
            </button>
            <a
              href={generation.result_url!}
              download
              className="rounded-full border border-white/10 bg-white/5 p-2 transition-colors hover:bg-white/10"
            >
              <Download className="h-5 w-5 text-white" />
            </a>
            <button
              onClick={onDelete}
              className="rounded-full border border-red-500/20 bg-white/5 p-2 transition-colors hover:bg-red-500/20"
            >
              <Trash2 className="h-5 w-5 text-red-400" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
